import { useCallback, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import ConfirmationDialog from '@components/Dialog/Confirm';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { SelectionModel } from '@components/DataTable/types';
import { DELETE_BRANDS, UPDATE_BRANDS_FEATURED } from '@gql/brand';
import { usePageSize } from '@hooks/usePageSize';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { useProId } from '@contexts/ProIdContext';
import BrandCreateDialogForm from './components/BrandCreateDialogForm';
import BrandsFilter from './BrandsFilter';
import BrandsDataTable from './BrandsDataTable';
import { useGetBrands, BrandsFilter as BrandsFilterType } from './hooks/useGetBrands';

const BrandList = () => {
    const { showSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { selectedProId } = useProId();

    const [page, setPage] = useState(0);
    const { pageSize, storePageSize } = usePageSize({ storageKey: 'brands-list-page-size' });
    const [query, setQuery] = useState('');
    const [filter, setFilter] = useState<BrandsFilterType>({});
    const [selectionModel, setSelectionModel] = useState<SelectionModel>({
        selectedRows: [],
        isAllSelected: false,
        isSomeSelected: false,
    });
    const [openModal, setOpenModal] = useState({
        open: false,
        title: '',
        description: '',
        action: '',
        cancelTitle: '',
        confirmTitle: '',
    });
    const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
    const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

    const { brands, total: totalDocs, loading, isLoadingMore, hasMore, loadMore, refetch } = useGetBrands(query, page, pageSize, filter);

    const [deleteBrands] = useMutation(DELETE_BRANDS);
    const [updateBrandsFeatured, { loading: isUpdatingFeatured }] = useMutation(UPDATE_BRANDS_FEATURED);

    const proId = user?.adminProId ?? selectedProId ?? '';

    const selectedIds = selectionModel.selectedRows.map(id => id.toString());

    useEffect(() => {
        setSelectionModel({ selectedRows: [], isAllSelected: false, isSomeSelected: false });
    }, [page, pageSize, query, filter]);

    const handleQueryChange = useCallback((value: string) => {
        setQuery(value);
        setPage(0);
    }, []);

    const handleFilterChange = useCallback((newFilter: BrandsFilterType) => {
        setFilter(newFilter);
        setPage(0);
    }, []);

    const requestDelete = useCallback((ids: string[]) => {
        setPendingDeleteIds(ids);
        setOpenModal({
            open: true,
            title: 'Supprimer des marques',
            description: `Êtes-vous sûr de vouloir supprimer ${ids.length > 1 ? 'ces marques' : 'cette marque'} ?`,
            action: 'delete',
            cancelTitle: 'Annuler',
            confirmTitle: 'Supprimer',
        });
    }, []);

    const handleBulkFeaturedUpdate = async (isFeatured: boolean) => {
        if (selectedIds.length === 0) {
            showSnackbar('Sélectionnez au moins une marque.', 'warning');
            return;
        }
        try {
            await updateBrandsFeatured({ variables: { ids: selectedIds, isFeatured } });
            showSnackbar('Statut "en vedette" mis à jour.', 'success');
            await refetch();
            setSelectionModel({ selectedRows: [], isAllSelected: false, isSomeSelected: false });
        } catch (err: any) {
            showSnackbar(err?.message || 'Échec de la mise à jour du statut en vedette.', 'error');
        }
    };

    const handleModalAction = async () => {
        if (openModal.action !== 'delete') return;
        setIsLoadingSubmit(true);
        try {
            await deleteBrands({ variables: { ids: pendingDeleteIds } });
            showSnackbar('Marque(s) supprimée(s) avec succès.', 'success');
            setSelectionModel(prev => ({
                ...prev,
                selectedRows: prev.selectedRows.filter(id => !pendingDeleteIds.includes(id.toString())),
            }));
            await refetch();
        } catch (err: any) {
            showSnackbar(err?.message || 'Échec de la suppression de la/les marque(s).', 'error');
        } finally {
            setIsLoadingSubmit(false);
            setOpenModal({ open: false, title: '', description: '', action: '', cancelTitle: '', confirmTitle: '' });
            setPendingDeleteIds([]);
        }
    };

    const handleCreateSuccess = (brandId: string) => {
        setIsCreateDialogOpen(false);
        navigate(`/brands/${brandId}/detail`);
    };

    return (
        <Box pt={0} minHeight="calc(100vh - 118px)" sx={{ display: 'flex', flexDirection: 'column' }}>
            <BrandCreateDialogForm
                open={isCreateDialogOpen}
                onClose={() => setIsCreateDialogOpen(false)}
                onSuccess={handleCreateSuccess}
            />
            <ConfirmationDialog
                details={openModal}
                setOpen={setOpenModal}
                handleModalAction={handleModalAction}
                isLoadingSubmit={isLoadingSubmit}
            />
            <BrandsFilter
                totalDocs={totalDocs}
                selectedIds={selectedIds}
                isUpdatingFeatured={isUpdatingFeatured}
                proId={proId}
                filter={filter}
                onQueryChange={handleQueryChange}
                onFilterChange={handleFilterChange}
                onAddBrand={() => setIsCreateDialogOpen(true)}
                onBulkFeaturedUpdate={handleBulkFeaturedUpdate}
                onBulkDeleteSelected={() => requestDelete(selectedIds)}
            />
            <BrandsDataTable
                brands={brands}
                totalDocs={totalDocs}
                loading={loading}
                isLoadingMore={isLoadingMore}
                onScrollEnd={hasMore ? loadMore : undefined}
                page={page}
                rowsPerPage={pageSize}
                selectionModel={selectionModel}
                onPageChange={setPage}
                onPageSizeChange={newSize => {
                    storePageSize(newSize);
                    setPage(0);
                }}
                onSelectionModelChange={setSelectionModel}
                onDeleteRow={id => requestDelete([id])}
            />
        </Box>
    );
};

export default BrandList;
